import {
  CC_OVERVIEW_ATTACHED_LOGICAL_PATHS,
  CLOSING_BAND_PATHS,
} from "@/lib/cc-nav-route-constants";
import { devImageLibraryPath } from "@/lib/dev/image-library";
import { isKnownSitePath, normalizeLogicalPathname } from "@/lib/cc-site-paths";

/** Overview FAQ closing band backdrop (Odysseus sirens). JPG-only in dev-library. */
export const CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_SRC =
  devImageLibraryPath("odysseus-sirens-fog") ??
  "/assets/imgs/dev-library/odysseus-sirens-fog.jpg";

export const CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_WIDTH = 1024;
export const CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_HEIGHT = 682;

/** @deprecated Use {@link CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_SRC}. */
export const CC_OVERVIEW_ATTACHED_FOOTER_ANTIQUE_PHOTO_SRC =
  CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_SRC;

/** @deprecated Use {@link CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_WIDTH}. */
export const CC_OVERVIEW_ATTACHED_FOOTER_ANTIQUE_PHOTO_WIDTH =
  CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_WIDTH;

/** @deprecated Use {@link CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_HEIGHT}. */
export const CC_OVERVIEW_ATTACHED_FOOTER_ANTIQUE_PHOTO_HEIGHT =
  CC_OVERVIEW_ATTACHED_CLOSING_BAND_PHOTO_HEIGHT;

/**
 * Official footer layout variants for site chrome.
 *
 * - `overview-attached` — marketing routes (Odysseus CTA closing band + attached antique footer).
 * - `article-detached` — legacy inset footer card (contact light-split override only).
 */
export type CcFooterLayout = "overview-attached" | "article-detached";

export const CC_FOOTER_LAYOUT = {
  overviewAttached: "overview-attached",
  articleDetached: "article-detached",
} as const satisfies Record<"overviewAttached" | "articleDetached", CcFooterLayout>;

export type CcFooterLayoutMeta = {
  label: string;
  description: string;
};

export const CC_FOOTER_LAYOUT_META: Record<CcFooterLayout, CcFooterLayoutMeta> = {
  [CC_FOOTER_LAYOUT.overviewAttached]: {
    label: "Overview · attached",
    description:
      "Odysseus CTA closing band flows into an attached footer frame with contact antique atmosphere.",
  },
  [CC_FOOTER_LAYOUT.articleDetached]: {
    label: "Article · detached",
    description:
      "Light page body ends above a detached dark footer card with inline antique atmosphere.",
  },
};

export type CcSiteFooterChrome = {
  layout: CcFooterLayout;
  continuesClosingBand: boolean;
  useAntiquePhoto: boolean;
};

export function isOverviewAttachedFooter(layout: CcFooterLayout): boolean {
  return layout === CC_FOOTER_LAYOUT.overviewAttached;
}

/** Main modifiers when a page registers an attached Odysseus closing band into `<footer>`. */
export const CC_MAIN_ATTACHED_FOOTER_CLOSING_BAND_CLASS =
  "cc-main--closing-band cc-main--footer-attached";

export function ccMainAttachedFooterClosingBand(...pageModifiers: string[]): string {
  return ["cc-main", ...pageModifiers.filter(Boolean), "cc-main--closing-band", "cc-main--footer-attached"].join(
    " ",
  );
}

/** Attached footer seam only — no Odysseus closing CTA band (contact / 404 parity). */
export function ccMainAttachedFooterSeam(...pageModifiers: string[]): string {
  return ["cc-main", ...pageModifiers.filter(Boolean), "cc-main--footer-attached"].join(" ");
}

function overviewAttachedChrome(useAntiquePhoto: boolean): CcSiteFooterChrome {
  return {
    layout: CC_FOOTER_LAYOUT.overviewAttached,
    continuesClosingBand: false,
    useAntiquePhoto,
  };
}

function articleDetachedChrome(): CcSiteFooterChrome {
  return {
    layout: CC_FOOTER_LAYOUT.articleDetached,
    continuesClosingBand: false,
    useAntiquePhoto: true,
  };
}

/**
 * Pathname-based footer chrome for marketing routes (contact variant overrides layout only).
 */
export function resolveSiteFooterChrome(pathname: string): CcSiteFooterChrome {
  const path = normalizeLogicalPathname(pathname);

  if (!isKnownSitePath(pathname)) {
    return overviewAttachedChrome(false);
  }

  if (path === "/contact") {
    return overviewAttachedChrome(false);
  }

  return overviewAttachedChrome(true);
}

/** @deprecated Prefer {@link resolveSiteFooterChrome}. */
export function footerLayout(pathname: string): CcFooterLayout {
  return resolveSiteFooterChrome(pathname).layout;
}

export function footerContinuesClosingBand(pathname: string): boolean {
  const path = normalizeLogicalPathname(pathname);

  if (CC_OVERVIEW_ATTACHED_LOGICAL_PATHS.some((p) => path === p)) {
    return false;
  }

  return CLOSING_BAND_PATHS.some((p) => path === p);
}

/** Antique ruins photo in footer — on overview hubs + article pages; off on 404 and contact. */
export function footerUsesAntiquePhoto(pathname: string): boolean {
  return resolveSiteFooterChrome(pathname).useAntiquePhoto;
}
